import { Dropdown, DropdownProps, makeStyles, OptionGroup } from '@fluentui/react-components';
import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { RestoreSnapshotDropdownOption, SnapshotType } from './model';
import RestoreSnapshotDropdownItem from './restore-snapshot-dropdown-item';

export interface RestoreSnapshotDropdownProps extends DropdownProps {
    options?: RestoreSnapshotDropdownOption[];
}

const messages = defineMessages({
    dropdownAriaLabel: {
        id: 'RestoreSnapshotDialog_Dropdown_AriaLabel',
        defaultMessage: 'Please choose the snapshot to restore',
        description: 'Aria label for dropdown in the "Snapshot Restoration" dialog',
    },
    dropdownPlaceholder: {
        id: 'RestoreSnapshotDialog_Dropdown_Placeholder',
        defaultMessage: 'Select a restore point',
        description: 'Placeholder for dropdown in the "Snapshot Restoration" dialog',
    },
    manualSnapshotLabel: {
        id: 'RestoreSnapshotDialog_ManualSnapshot_Label',
        defaultMessage: 'Manual Snapshot',
        description: 'Label for the manual snapshot option group in the dropdown',
    },
    automaticSnapshotLabel: {
        id: 'RestoreSnapshotDialog_AutomaticSnapshot_Label',
        defaultMessage: 'Automatic Snapshot',
        description: 'Label for the automatic snapshot option group in the dropdown',
    },
});

/**
 * Styles
 */

const useStyles = makeStyles({
    root: {
        position: 'relative',
        width: '100%',
        zIndex: 0,
    },
    dropdown: {
        width: '100%',
        position: 'static',
    },
});

/**
 * END Styles
 */

export const RestoreSnapshotDropdown: React.FC<RestoreSnapshotDropdownProps> = (
    props: RestoreSnapshotDropdownProps
) => {
    // Intl hooks
    const { formatMessage } = useIntl();

    const { options } = props;

    const styles = useStyles();

    return (
        <div className={styles.root}>
            <Dropdown
                className={styles.dropdown}
                placeholder={formatMessage(messages.dropdownPlaceholder)}
                aria-label={formatMessage(messages.dropdownAriaLabel)}
                positioning={{
                    position: 'below',
                    align: 'start',
                    pinned: true,
                    offset: { mainAxis: 4 },
                    overflowBoundary: typeof document !== 'undefined' ? document.body : undefined,
                }}
                {...props}
            >
                <OptionGroup label={formatMessage(messages.manualSnapshotLabel)}>
                    {options
                        ?.filter((option) => option.snapshotType === SnapshotType.Manual)
                        .sort((a, b) => new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime())
                        .slice(0, 1)
                        .map((option) => (
                            <RestoreSnapshotDropdownItem key={option.snapshotId} snapshot={option} />
                        ))}
                </OptionGroup>
                <OptionGroup label={formatMessage(messages.automaticSnapshotLabel)}>
                    {options
                        ?.filter((option) => option.snapshotType === SnapshotType.Automatic)
                        .map((option) => (
                            <RestoreSnapshotDropdownItem key={option.snapshotId} snapshot={option} />
                        ))}
                </OptionGroup>
            </Dropdown>
        </div>
    );
};

export default RestoreSnapshotDropdown;
